import type { CrudService, GetAllParams, GetAllResponse } from '../../../MasterCrud';
import type { DynamicField } from '../../core/types/DynamicField.types';
import type { DynamicEntity } from '../../core/types/DynamicEntity.types';
import type { DynamicEntitiesFetcher } from '../../core/api/fetcher';
import { resolveFetcher } from '../../core/api/fetcher';
import { DynamicEntitiesHttpError } from '../../core/api/DynamicEntitiesHttpError';

// We need Feature type definition. 
// Ideally we should have a shared type or define it here.
export interface Feature {
    id: string;
    code: string;
    name: string;
    description?: string;
    type: 'Master' | 'Document' | 'Transaction'; // Enum from backend
    active: boolean;
    allowsPlansAndCriteria: boolean;
    hasFileManagement?: boolean;
    createdAt?: string;
    updatedAt?: string;
    featureEntities?: FeatureEntity[];
}

export interface FeatureEntity {
    id: string;
    featureId: string;
    dynamicEntityId: string;
    renderMode: 'tab' | 'modal' | 'inline';
    order: number;
    properties?: Record<string, any>;
    dynamicEntity?: DynamicEntity;
}



export interface SystemEntityStatus {
    id: string;
    code: string;
    description?: string;
    isEnabled: boolean;
    enabledSystemEntityId?: string;
    renderMode?: 'tab' | 'inline';
    enabledBy?: string;
    enabledAt?: string;
}

export interface EnableSystemEntityResult {
    enabledSystemEntityId: string;
    featureEntityId: string;
}

export class FeatureService implements CrudService<Feature> {
    private baseUrl: string;
    private http: DynamicEntitiesFetcher;

    /**
     * @param baseUrl Raíz de la API.
     * @param fetcher Transporte del anfitrión (con `Authorization` y demás
     * cabeceras). Ausente → `fetch` global, igual que antes.
     */
    constructor(baseUrl: string, fetcher?: DynamicEntitiesFetcher) {
        this.baseUrl = baseUrl;
        this.http = resolveFetcher(fetcher);
    }

    async getAll(params: GetAllParams): Promise<GetAllResponse<Feature>> {
        // Backend currently only has GetAll (List<Feature>). Paging is not implemented in Controller yet.
        // We will mock paging on client side if needed or just return all.
        // For real prod, backend needs Paging.

        // Los errores se propagan a propósito: devolver una lista vacía hacía que
        // un 401 se viera exactamente igual que "no hay maestros creados".
        const response = await this.http(`${this.baseUrl}/features`);
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch features: ${response.status} ${response.statusText}`, response);

        const allData = await response.json();

        // Client-side pagination/filtering for MVP
        // TODO: Implement server-side pagination in backend
        let data = [...allData];

        if (params.search) {
            const term = params.search.toLowerCase();
            data = data.filter(f =>
                f.code.toLowerCase().includes(term) ||
                f.description?.toLowerCase().includes(term) ||
                f.name?.toLowerCase().includes(term)
            );
        }

        const total = data.length;
        const start = (params.page - 1) * params.limit;
        const paginatedData = data.slice(start, start + params.limit);

        return {
            data: paginatedData,
            total
        };
    }

    async getById(id: string | number): Promise<Feature> {
        const response = await this.http(`${this.baseUrl}/features/${id}`);
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch feature: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async getByCode(code: string): Promise<Feature> {
        const response = await this.http(`${this.baseUrl}/dynamic-entities/features/by-code/${code}`);
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch feature by code: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async getByPath(path: string): Promise<Feature> {
        const url = `${this.baseUrl}/dynamic-entities/features/by-path/${path}`;
        const response = await this.http(url);
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch feature by path: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async create(data: Partial<Feature>): Promise<Feature> {
        const response = await this.http(`${this.baseUrl}/features`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to create feature: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async update(id: string | number, feature: Partial<Feature>): Promise<Feature> {
        const response = await this.http(`${this.baseUrl}/features/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(feature)
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to update feature: ${response.status} ${response.statusText}`, response);
        // Return dummy or actual if needed, CrudService expects return
        // Cast to Feature for now, assuming partial update returns full or we merge
        return feature as Feature;
    }

    async setFileManagement(id: string, hasFileManagement: boolean): Promise<void> {
        const response = await this.http(`${this.baseUrl}/features/${id}/file-management`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(hasFileManagement)
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to update file management flag: ${response.status} ${response.statusText}`, response);
    }

    async delete(id: string | number): Promise<void> {
        const response = await this.http(`${this.baseUrl}/features/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to delete feature: ${response.status} ${response.statusText}`, response);
    }

    // Entity Management
    async createEntity(entity: Partial<DynamicEntity>): Promise<DynamicEntity> {
        const response = await this.http(`${this.baseUrl}/dynamic-entities`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(entity)
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to create entity: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async updateEntity(id: string, entity: Partial<DynamicEntity>): Promise<void> {
        const response = await this.http(`${this.baseUrl}/dynamic-entities/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(entity)
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to update entity: ${response.status} ${response.statusText}`, response);
    }

    async deleteEntity(id: string): Promise<void> {
        const response = await this.http(`${this.baseUrl}/dynamic-entities/${id}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to delete entity: ${response.status} ${response.statusText}`, response);
    }

    // FeatureEntity Links
    async addFeatureEntity(featureId: string, featureEntity: Partial<FeatureEntity>): Promise<FeatureEntity> {
        const response = await this.http(`${this.baseUrl}/features/${featureId}/entities`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(featureEntity)
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to add entity to feature: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async updateFeatureEntity(featureId: string, dynamicEntityId: string, featureEntity: Partial<FeatureEntity>): Promise<void> {
        const response = await this.http(`${this.baseUrl}/features/${featureId}/entities/${dynamicEntityId}`, {
            method: 'PUT', // or PATCH
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(featureEntity)
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to update feature entity link: ${response.status} ${response.statusText}`, response);
    }

    async removeFeatureEntity(featureId: string, dynamicEntityId: string): Promise<void> {
        const response = await this.http(`${this.baseUrl}/features/${featureId}/entities/${dynamicEntityId}`, {
            method: 'DELETE'
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to remove entity from feature: ${response.status} ${response.statusText}`, response);
    }

    // Fields
    async saveFields(entityId: string, fields: DynamicField[]): Promise<void> {
        const response = await this.http(`${this.baseUrl}/dynamic-entities/${entityId}/fields`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(fields)
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error("Fields Save Validation Error:", errorText);
            throw new DynamicEntitiesHttpError(`Failed to save fields: ${response.status} ${response.statusText} — ${errorText}`, response);
        }
    }

    // Rules
    async saveRules(entityId: string, rules: any[]): Promise<void> {
        const response = await this.http(`${this.baseUrl}/dynamic-entities/${entityId}/rules`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(rules)
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error("Rules Save Validation Error:", errorText);
            throw new DynamicEntitiesHttpError(`Failed to save rules: ${response.status} ${response.statusText} — ${errorText}`, response);
        }
    }

    // System Entity Management
    async getSystemEntities(featureId: string): Promise<SystemEntityStatus[]> {
        const response = await this.http(`${this.baseUrl}/features/${featureId}/system-entities`);
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to fetch system entities: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async enableSystemEntity(
        featureId: string,
        dynamicEntityId: string,
        renderMode: 'tab' | 'inline'
    ): Promise<EnableSystemEntityResult> {
        const response = await this.http(`${this.baseUrl}/features/${featureId}/system-entities/enable`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ dynamicEntityId, renderMode })
        });
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to enable system entity: ${response.status} ${response.statusText}`, response);
        return response.json();
    }

    async disableSystemEntity(featureId: string, enabledSystemEntityId: string): Promise<void> {
        const response = await this.http(
            `${this.baseUrl}/features/${featureId}/system-entities/${enabledSystemEntityId}/disable`,
            { method: 'DELETE' }
        );
        if (!response.ok) throw new DynamicEntitiesHttpError(`Failed to disable system entity: ${response.status} ${response.statusText}`, response);
    }
}
